// src/pages/DoctorHomePage.jsx
// Route: /doctor/home — the doctor's landing screen.
//
// A cockpit, not a directory: today's schedule, what needs the doctor now
// (open critical escalations, high-risk patients), and what's coming up.
// Built from endpoints the doctor portal already has; nothing here is new
// server state.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import DistributionBar from '../components/DistributionBar';
import ReportReviewCard from '../components/ReportReviewCard';
import NextPatientCard from '../components/doctor/NextPatientCard';
import { useAuth } from '../context/AuthContext';
import avatarTint from '../utils/avatarTint';
import {
  doctorPatientAPI,
  doctorAppointmentAPI,
  doctorEscalationAPI,
} from '../services/doctorApi';

const STATUS_META = {
  SCHEDULED: { label: 'Scheduled', cls: 'badge-confirmed' },
  CONFIRMED: { label: 'Confirmed', cls: 'badge-active'    },
  COMPLETED: { label: 'Completed', cls: 'badge-active'    },
  NO_SHOW:   { label: 'No show',   cls: 'badge-inactive'  },
  PENDING:   { label: 'Pending',   cls: 'badge-pending'   },
  ARRIVED:   { label: 'Arrived',   cls: 'badge-pending'   },
  IN_CONSULTATION: { label: 'In consultation', cls: 'badge-purple' },
};

const initials = (first = '', last = '') =>
  `${first[0] || ''}${last[0] || ''}`.toUpperCase();

const fmtTime = v => {
  const m = String(v || '').match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

const fmtDay = v => new Date(v).toLocaleDateString('en-IN',
  { weekday: 'short', day: 'numeric', month: 'short' });

// Local calendar date as YYYY-MM-DD (toISOString would shift it into UTC).
const localIso = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtTriggered = v => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const unwrap = res => res?.data?.content || res?.data || [];

function AppointmentRow({ a, onOpen, showDate }) {
  const status = STATUS_META[a.status] || STATUS_META.SCHEDULED;
  const p = a.patient || {};
  return (
    <button type="button" className="card-row" onClick={() => onOpen(a)}
      aria-label={`Open visit with ${p.firstName} ${p.lastName}`}>
      <div className="card-row-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={`avatar avatar--sm ${avatarTint(p.firstName || '')}`} aria-hidden="true">
            {initials(p.firstName, p.lastName)}
          </div>
          <div>
            <div className="card-row-title">{p.firstName} {p.lastName}</div>
            <div className="card-row-meta">
              {showDate && `${fmtDay(a.appointmentDate)} · `}
              {fmtTime(a.appointmentTime) || '—'} · {(a.appointmentType || 'Consultation').replace(/_/g, ' ').toLowerCase()}
            </div>
          </div>
        </div>
      </div>
      <span className={`badge ${status.cls}`}>{status.label}</span>
    </button>
  );
}

export default function DoctorHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [escalations,  setEscalations]  = useState([]);
  const [patients,     setPatients]     = useState([]);
  const [cohortStats,  setCohortStats]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [partial,      setPartial]      = useState(false);

  useEffect(() => {
    document.title = 'Dashboard — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [apptRes, escRes, patRes] = await Promise.allSettled([
        doctorAppointmentAPI.list(user?.email, 'all'),
        doctorEscalationAPI.list({ view: 'ACTIVE' }),
        doctorPatientAPI.list({ doctorEmail: user?.email, page: 0, size: 500 }),
      ]);
      if (cancelled) return;
      setAppointments(apptRes.status === 'fulfilled' ? unwrap(apptRes.value) : []);
      setEscalations(escRes.status === 'fulfilled' ? unwrap(escRes.value) : []);
      setPatients(patRes.status === 'fulfilled' ? (patRes.value.data?.patients || []) : []);
      setCohortStats(patRes.status === 'fulfilled' ? (patRes.value.data?.stats || null) : null);
      setPartial([apptRes, escRes, patRes].some(r => r.status === 'rejected'));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const todayIso = localIso();

  const { today, upcoming } = useMemo(() => {
    const byWhen = (a, b) =>
      (a.appointmentDate + (a.appointmentTime || '')).localeCompare(b.appointmentDate + (b.appointmentTime || ''));
    const live = appointments.filter(a => a.appointmentDate && a.status !== 'CANCELLED');
    return {
      today: live.filter(a => a.appointmentDate === todayIso).sort(byWhen),
      upcoming: live
        .filter(a => a.appointmentDate > todayIso && a.status !== 'COMPLETED' && a.status !== 'NO_SHOW')
        .sort(byWhen).slice(0, 6),
    };
  }, [appointments, todayIso]);

  const nextUp = today.find(a => a.status !== 'COMPLETED' && a.status !== 'NO_SHOW');
  const toSee  = today.filter(a => a.status !== 'COMPLETED' && a.status !== 'NO_SHOW').length;
  const highRisk = patients.filter(p => p.triagePriority === 'HIGH');
  // The escalation payload's patientName can come back blank, so fall back to
  // the doctor's own patient list, which already carries every name.
  const patientName = e => {
    if (e.patientName) return e.patientName;
    const p = patients.find(x => x.id === e.patientId);
    return p ? `${p.firstName} ${p.lastName}`.trim() : 'Patient';
  };
  const unacked = escalations.filter(e => ['PENDING', 'NOTIFIED', 'ESCALATED'].includes(e.status));

  // One row per patient: the same patient tends to have many repeat alerts, so
  // show how many and surface the most recent one.
  const alertGroups = useMemo(() => {
    const byPatient = new Map();
    unacked.forEach(e => {
      const key = e.patientId || e.id;
      const g = byPatient.get(key) || { key, patientId: e.patientId, alerts: [] };
      g.alerts.push(e);
      byPatient.set(key, g);
    });
    return [...byPatient.values()]
      .map(g => {
        const alerts = [...g.alerts].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
        return { ...g, alerts, latest: alerts[0] };
      })
      .sort((a, b) => String(b.latest.createdAt).localeCompare(String(a.latest.createdAt)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escalations]);
  // The Needs you now card lists only patients with 2+ unacknowledged critical
  // alerts, the ones most likely to be slipping. The count on the card and the
  // Escalations page (one click away) still cover every alert.
  const repeatAlerts = alertGroups.filter(g => g.alerts.length >= 2);

  // Cohort figures (formerly the Analytics page), minus escalations (own page now).
  // "High risk" is already the High-risk patients card above, so it is not
  // repeated. Appointment buckets are the server's `bucket` field (DEF-017/025):
  // counting from status and date here would let a cancelled future appointment
  // read as Upcoming in one view and Past in another.
  const cohortSize     = cohortStats?.total ?? patients.length;
  const pendingActions = cohortStats?.pendingActions ?? 0;
  const riskCounts = patients.reduce((acc, p) => {
    const level = (p.triagePriority || 'UNKNOWN').toUpperCase();
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {});
  const bucketCounts = appointments.reduce((acc, a) => {
    const bucket = a.bucket || 'UPCOMING';
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

  // Whole-card links: a click on an inner button (a visit row, the footer
  // button) keeps its own action instead of also opening the list.
  const openList = (e, to) => { if (!e.target.closest('button')) navigate(to); };
  const openVisit = a => navigate(`/doctor/appointments/${a.id}`);
  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  })();

  // `key` places each tile in the grid (phone and desktop, see index.css);
  // `short` is its label in the phone stat strip (Needs you now keeps its full
  // label there).
  const stats = [
    { key: 'today', label: 'Today', short: 'Today', value: today.length, sub: `${toSee} still to see`, icon: 'calendar', tone: 'blue',
      to: '/doctor/appointments' },
    { key: 'urgent', label: 'Needs you now', value: alertGroups.length, sub: `patients · ${unacked.length} unacknowledged critical alert${unacked.length === 1 ? '' : 's'}`, icon: 'alert', tone: 'red', alert: alertGroups.length > 0,
      to: '/doctor/escalations', repeat: true },
    { key: 'risk', label: 'High-risk patients', short: 'High-risk', value: highRisk.length, sub: `of ${patients.length} assigned`, icon: 'people', tone: 'amber', to: '/doctor/patients' },
    { key: 'upcoming', label: 'Coming up', short: 'Coming up', value: upcoming.length, sub: 'next appointments', icon: 'clock', tone: 'green', to: '/doctor/appointments' },
    { key: 'cohort', label: 'Patients in cohort', short: 'Cohort', value: cohortSize, sub: 'assigned to you', icon: 'people', tone: 'blue', to: '/doctor/patients' },
    { key: 'pending', label: 'Pending actions', short: 'Pending', value: pendingActions, sub: 'action items open', icon: 'clipboard', tone: 'amber', to: '/doctor/patients' },
  ];

  return (
    <AppShell breadcrumbs={[{ label: 'Dashboard' }]}>
      <div className="dhome">
        <div className="page-header fade-in-up">
          <div>
            <div className="page-title">{greeting}, {user?.name?.split(' ')[0] || 'Doctor'}</div>
            <div className="page-subtitle">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        {partial && (
          <div className="alert alert-error">Some of this page couldn't load — numbers may be incomplete.</div>
        )}

        <div className="stats-row dhome-stats fade-in-up delay-1">
          {stats.map(s => {
            const body = (
              <>
                <div className="stat-card-head">
                  <div className={`stat-icon stat-icon--${s.tone}`} aria-hidden="true">
                    <Icon name={s.icon} size={20} />
                  </div>
                </div>
                <div className="stat-label">
                  {s.short ? (
                    <>
                      <span className="dhome-label-long">{s.label}</span>
                      <span className="dhome-label-short" aria-hidden="true">{s.short}</span>
                    </>
                  ) : s.label}
                </div>
                <div className="stat-value">{loading ? '—' : s.value}</div>
                <div className="stat-change">{s.sub}</div>
                {s.repeat && !loading && (
                  <div className="card-list dhome-urgent-list" style={{ marginTop: 12 }}>
                    {repeatAlerts.slice(0, 1).map(g => {
                      const when = fmtTriggered(g.latest.createdAt) || '—';
                      const detail = g.latest.acuteAlertDetail || `Risk level ${g.latest.riskLevel || 'HIGH'}`;
                      // Clamped to two lines (phone and desktop); the title keeps the full text.
                      return (
                        <div key={g.key} className="card-row-body">
                          <div className="card-row-title">
                            {patientName(g.latest)}{' '}
                            <span className="badge badge-critical">Critical × {g.alerts.length}</span>
                          </div>
                          <div className="card-row-meta dhome-urgent-latest" title={`Latest ${when} · ${detail}`}>
                            Latest {when} · {detail}
                          </div>
                        </div>
                      );
                    })}
                    {repeatAlerts.length > 1 && (
                      <div className="card-row-meta">+{repeatAlerts.length - 1} more patient{repeatAlerts.length - 1 === 1 ? '' : 's'} with 2+ alerts</div>
                    )}
                    {repeatAlerts.length === 0 && alertGroups.length > 0 && (
                      <div className="card-row-meta">No patient has 2+ critical alerts.</div>
                    )}
                    {alertGroups.length > 0 && <div className="field-label dhome-urgent-all">View all in Escalations →</div>}
                  </div>
                )}
              </>
            );
            const cls = `stat-card${s.alert ? ' stat-card--alert' : ''}`;
            return (
              <button key={s.label} type="button"
                className={`${cls} card--link dhome-stat dhome-stat--${s.key} ${s.repeat ? 'dhome-urgent' : 'dhome-cell'}`}
                onClick={() => navigate(s.to)}
                aria-label={`${s.label}: ${s.value}. Open ${s.to.split('/').pop()}`}>
                {body}
              </button>
            );
          })}
        </div>

        <NextPatientCard />

        <ReportReviewCard />

        <div className="detail-grid detail-grid--2">
          <section className="card fade-in-up delay-3 card--link" role="link" tabIndex={0}
            onClick={e => openList(e, '/doctor/patients')} onKeyDown={e => e.key === 'Enter' && openList(e, '/doctor/patients')}>
            <div className="card-title">Risk distribution</div>
            {loading ? <div className="skeleton skeleton--row" /> : (
              <DistributionBar
                total={cohortSize}
                emptyText="No patients in your cohort yet."
                rows={[
                  { label: 'High',     count: riskCounts.HIGH     || 0, color: 'var(--red)' },
                  { label: 'Moderate', count: riskCounts.MODERATE || 0, color: 'var(--amber)' },
                  { label: 'Low',      count: riskCounts.LOW      || 0, color: 'var(--green)' },
                  { label: 'Not triaged', count: (riskCounts.UNKNOWN || 0) + (riskCounts.NONE || 0), color: 'var(--border-strong)' },
                ]}
              />
            )}
          </section>

          <section className="card fade-in-up delay-3 card--link" role="link" tabIndex={0}
            onClick={e => openList(e, '/doctor/appointments')} onKeyDown={e => e.key === 'Enter' && openList(e, '/doctor/appointments')}>
            <div className="card-title">Appointments</div>
            {loading ? <div className="skeleton skeleton--row" /> : (
              <DistributionBar
                total={appointments.length}
                emptyText="No appointments yet."
                rows={[
                  { label: 'Upcoming',  count: bucketCounts.UPCOMING  || 0, color: 'var(--brand-dark)' },
                  { label: 'Past',      count: bucketCounts.PAST      || 0, color: 'var(--green)' },
                  { label: 'Cancelled', count: bucketCounts.CANCELLED || 0, color: 'var(--text-muted)' },
                ]}
              />
            )}
          </section>
        </div>
      </div>

    </AppShell>
  );
}
